import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Grid,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import { Link, useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import ctaImage1 from "../../../assets/cta-pattern.png";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import { showErrorToast } from "../../../components/common/ShowToast";
import { setTwoFaUsername } from "../../../store/slice/AuthSlice";
import apiService, { BaseURL } from "../../../service/Api";
import TermsModal from "../../../components/TermsModal";
import CtaSlideshow from "./CTASlideshow";

interface User {
  id: number;
  username: string;
  email: string;
  user_type: string;
}

interface SignupResponse {
  user: User;
  is_2fa_enabled: boolean;
  message: string;
}

const Signup = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [userType, setUserType] = useState("individual");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const ctaImages = [ctaImage1, ctaImage2];
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openTermsModal, setOpenTermsModal] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !email || !password || !confirmPassword || !userType) {
      showErrorToast("Please fill in all required fields.");
      return;
    }
    if (password !== confirmPassword) {
      showErrorToast("Passwords do not match.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      showErrorToast("Please enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      showErrorToast("Password must be at least 8 characters long.");
      return;
    }
    if (userType === "individual" && (!firstName || !lastName)) {
      showErrorToast(
        "First name and last name are required for individual users.",
      );
      return;
    }
    if (userType === "company_user" && !companyName) {
      showErrorToast("Company name is required for company users.");
      return;
    }
    if (!termsAccepted) {
      showErrorToast("You must agree to the Terms and Conditions.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        username,
        email,
        password,
        user_type: userType,
        ...(userType === "individual" && {
          first_name: firstName,
          last_name: lastName,
        }),
        ...(userType === "company_user" && {
          company_name: companyName,
        }),
      };

      const response = await apiService.post<SignupResponse>(
        "/api/authentication/signup/",
        payload,
        true,
      );

      dispatch(setTwoFaUsername(response.data.user.username));
      navigate("/setup-2fa");
    } catch (err: unknown) {
      const errorInfo = err as {
        message?: string;
        response?: { data?: { message?: string }; status?: number };
      };
      console.error("Signup error:", {
        message: errorInfo.message,
        response: errorInfo.response?.data,
        status: errorInfo.response?.status,
      });
      const errorMsg =
        errorInfo.response?.data?.message ||
        errorInfo.message ||
        "An error occurred. Please try again.";
      showErrorToast(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.07 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: colors.primary[900],
      height: "52px",
      "& fieldset": { borderColor: "divider" },
      "&:hover fieldset": { borderColor: "text.secondary" },
      "&.Mui-focused fieldset": { borderColor: "#6b4c3b" },
    },
    "& .MuiInputBase-input": { color: "text.primary", fontSize: "15px" },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: colors.primary[900],
        position: "relative",
        border: isStaging ? "10px solid #FFBB00" : "none",
        boxSizing: "border-box",
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: colors.primary[900],
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
          }}
        >
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 520, width: "100%" }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      mb: 0.5,
                      fontSize: { xs: "24px", md: "28px" },
                    }}
                  >
                    Create Account
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: "15px",
                      mb: 3,
                      lineHeight: 1.6,
                    }}
                  >
                    Fill in your credentials to create an account
                  </Typography>
                </motion.div>

                {/* User type selection */}
                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        value={userType}
                        onChange={(e) => setUserType(e.target.value)}
                      >
                        <FormControlLabel
                          value="individual"
                          control={
                            <Radio
                              sx={{
                                color: "text.secondary",
                                "&.Mui-checked": { color: "#6b4c3b" },
                              }}
                            />
                          }
                          label="Individual"
                          sx={{ mr: 4 }}
                        />
                        <FormControlLabel
                          value="company_user"
                          control={
                            <Radio
                              sx={{
                                color: "text.secondary",
                                "&.Mui-checked": { color: "#6b4c3b" },
                              }}
                            />
                          }
                          label="Company"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </motion.div>

                {/* First + Last Name row (for individual) */}
                {userType === "individual" && (
                  <motion.div variants={itemVariants}>
                    <Grid container spacing={2} sx={{ mb: 2.5 }}>
                      <Grid size={{ xs: 6 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "14px",
                            mb: 1,
                          }}
                        >
                          First Name
                        </Typography>
                        <TextField
                          placeholder="First Name"
                          variant="outlined"
                          fullWidth
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          disabled={isLoading}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 6 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                            fontSize: "14px",
                            mb: 1,
                          }}
                        >
                          Last Name
                        </Typography>
                        <TextField
                          placeholder="Last Name"
                          variant="outlined"
                          fullWidth
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          disabled={isLoading}
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>
                  </motion.div>
                )}

                {/* Company name (for company_user) */}
                {userType === "company_user" && (
                  <motion.div variants={itemVariants}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        fontSize: "14px",
                        mb: 1,
                      }}
                    >
                      Company Name
                    </Typography>
                    <TextField
                      placeholder="Enter your company name"
                      variant="outlined"
                      fullWidth
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      disabled={isLoading}
                      sx={{ ...fieldSx, mb: 2.5 }}
                    />
                  </motion.div>
                )}

                {/* Username */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Username
                  </Typography>
                  <TextField
                    placeholder="Enter your username"
                    variant="outlined"
                    fullWidth
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    sx={{ ...fieldSx, mb: 2.5 }}
                  />
                </motion.div>

                {/* Email */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    placeholder="Enter your email address"
                    variant="outlined"
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    sx={{ ...fieldSx, mb: 2.5 }}
                  />
                </motion.div>

                {/* New Password */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    New Password
                  </Typography>
                  <TextField
                    placeholder="••••••••••••••••"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                            sx={{ color: "text.secondary" }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...fieldSx, mb: 2.5 }}
                  />
                </motion.div>

                {/* Confirm Password */}
                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: "14px",
                      mb: 1,
                    }}
                  >
                    Confirm Password
                  </Typography>
                  <TextField
                    placeholder="••••••••••••••••"
                    variant="outlined"
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                            disabled={isLoading}
                            sx={{ color: "text.secondary" }}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...fieldSx, mb: 2 }}
                  />
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Box sx={{ mb: 2.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          sx={{
                            color: "text.secondary",
                            "&.Mui-checked": { color: "#6b4c3b" },
                          }}
                          disabled={isLoading}
                        />
                      }
                      label={
                        <Typography sx={{ color: "text.primary", fontSize: "14px" }}>
                          {"I agree to the "}
                          <span
                            style={{
                              color: "#6b4c3b",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            onClick={() => setOpenTermsModal(true)}
                          >
                            Terms and Conditions
                          </span>
                        </Typography>
                      }
                    />
                  </Box>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "#6b4c3b",
                      textTransform: "none",
                      padding: "14px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "none",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#55392d",
                        boxShadow: "none",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(107,76,59,0.4)",
                        color: "#fff",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    align="center"
                    sx={{ color: "text.secondary", fontSize: "14px", mt: 3 }}
                  >
                    {"Already have an account? "}
                    <Link
                      to="/login"
                      style={{
                        color: "#6b4c3b",
                        fontWeight: 600,
                        textDecoration: "none",
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </motion.div>
              </Box>
            </motion.div>
          </Box>
        </Grid>
        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: colors.primary[900],
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>

      {/* Terms modal */}
      <TermsModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setOpenTermsModal(false);
        }}
      />
    </Box>
  );
};

export default Signup;
